import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Loader,
  Banner,
  Breadcrumbs,
  Button,
  Input,
  Label,
  LayerCard,
  Select,
  Switch,
  Tabs,
  Text,
  useKumoToastManager,
} from '@cloudflare/kumo'
import { BuildingsIcon, HouseIcon } from '@phosphor-icons/react'
import { apiRequest, ApiError } from '../api/client'
import { useTranslation } from '../i18n'
import { PageHeader } from '../components/kumo/page-header/page-header'
import {
  listSchedules,
  getCompanySchedules,
  putCompanySchedules,
  type Schedule,
} from '../api/schedules'
import { DomainTagInput } from '../components/DomainTagInput'
import { CompanyEnrollmentPanel } from '../components/enrollment/company-enrollment-panel'
import type { Building, WifiNetwork } from '@hub-wifi/shared'

/* ------------------------------------------------------------------ */
/*  CompanyFormPage                                                     */
/* ------------------------------------------------------------------ */

interface CompanyFormData {
  name: string
  taxId: string
  domains: string[]
  email: string
  planType: string
  wifiNetworkIds: string[]
  buildingId: string
  openEnrollment: boolean
  skipEmailVerification: boolean
}

const emptyForm: CompanyFormData = {
  name: '',
  taxId: '',
  domains: [],
  email: '',
  planType: '',
  wifiNetworkIds: [],
  buildingId: '',
  openEnrollment: false,
  skipEmailVerification: false,
}

interface CompanyResponse {
  id: string
  name: string
  taxId: string
  domains: string[] | null
  email: string
  planType: string
  wifiNetworkIds: string[] | null
  buildingId?: string
  openEnrollment?: boolean
  skipEmailVerification?: boolean
}

export function CompanyFormPage() {
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const toasts = useKumoToastManager()

  const [form, setForm] = useState<CompanyFormData>(emptyForm)
  const [loading, setLoading] = useState(isEdit)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The API deliberately omits `password` from this payload (the PSK must
  // never reach the browser), so the type must say so too — the picker only
  // reads id and name.
  const [allNetworks, setAllNetworks] = useState<Omit<WifiNetwork, 'password'>[]>([])
  const [allBuildings, setAllBuildings] = useState<Building[]>([])
  const [allSchedules, setAllSchedules] = useState<Schedule[]>([])
  const [scheduleIds, setScheduleIds] = useState<string[]>([])
  const [activeTab, setActiveTab] = useState('general')

  useEffect(() => {
    async function fetchData() {
      try {
        const token = await getAccessTokenSilently()

        const fetches: Promise<unknown>[] = [
          apiRequest<Omit<WifiNetwork, 'password'>[]>('/api/companies/networks', { token }).then(
            setAllNetworks,
          ),
          apiRequest<Building[]>('/api/buildings', { token }).then(setAllBuildings),
          listSchedules(token).then(setAllSchedules),
        ]

        if (id) {
          fetches.push(
            apiRequest<CompanyResponse>(`/api/companies/${id}`, { token }).then((company) => {
              setForm({
                name: company.name,
                taxId: company.taxId,
                domains: company.domains ?? [],
                email: company.email,
                planType: company.planType,
                wifiNetworkIds: company.wifiNetworkIds ?? [],
                buildingId: company.buildingId ?? '',
                openEnrollment: company.openEnrollment ?? false,
                skipEmailVerification: company.skipEmailVerification ?? false,
              })
            }),
          )
          fetches.push(
            getCompanySchedules(id, token).then(({ scheduleIds: ids }) => setScheduleIds(ids)),
          )
        }

        await Promise.all(fetches)
      } catch (err) {
        setError(err instanceof ApiError ? err.message : t('common.error'))
      } finally {
        setLoading(false)
      }
    }

    void fetchData()
  }, [id, getAccessTokenSilently, t])

  function setField<K extends keyof CompanyFormData>(key: K, value: CompanyFormData[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)

    try {
      const token = await getAccessTokenSilently()

      if (isEdit && id) {
        await apiRequest(`/api/companies/${id}`, {
          method: 'PUT',
          token,
          body: form,
        })
        await putCompanySchedules(id, scheduleIds, token)
        toasts.add({ variant: 'success', title: t('companies.updateSuccess') })
      } else {
        const created = await apiRequest<{ id: string }>('/api/companies', {
          method: 'POST',
          token,
          body: form,
        })
        await putCompanySchedules(created.id, scheduleIds, token)
        toasts.add({ variant: 'success', title: t('companies.createSuccess') })
      }
      navigate('/companies')
    } catch (err) {
      toasts.add({
        variant: 'error',
        title: t('common.errorTitle'),
        description: err instanceof ApiError ? err.message : t('common.error'),
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Link icon={<BuildingsIcon size={14} />} href="/companies">
              {t('companies.title')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>
              {isEdit ? t('companies.editCompany') : t('companies.createCompany')}
            </Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={isEdit ? t('companies.editCompany') : t('companies.createCompany')}
        description={t('companies.pageDescription')}
      />

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader size="lg" />
        </div>
      )}

      {!loading && error && (
        <Banner variant="error">
          {t('common.errorPrefix')} {error}
        </Banner>
      )}

      {!loading && !error && (
        <LayerCard>
          <LayerCard.Primary>
            <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-4">
              <Tabs
                tabs={[
                  { value: 'general', label: t('companies.general') },
                  { value: 'selfEnrollment', label: t('companies.selfEnrollment') },
                ]}
                value={activeTab}
                onValueChange={setActiveTab}
              />

              {activeTab === 'general' && (
                <div className="flex flex-col gap-4 pt-2">
                  <div className="flex flex-col gap-1">
                    <Label>{t('companies.name')}</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => setField('name', e.target.value)}
                      required
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <Label>{t('companies.taxId')}</Label>
                    <Input
                      value={form.taxId}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '')
                        setField('taxId', digits)
                      }}
                      placeholder={t('companies.taxIdHint')}
                      required
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <Label>{t('companies.domains')}</Label>
                    <DomainTagInput
                      value={form.domains}
                      onChange={(domains) => setField('domains', domains)}
                      placeholder={t('companies.domainPlaceholder')}
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <Label>{t('companies.email')}</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setField('email', e.target.value)}
                      required
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <Label>{t('companies.planType')}</Label>
                    <Input
                      value={form.planType}
                      onChange={(e) => setField('planType', e.target.value)}
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <Label>{t('companies.building')}</Label>
                    <Select
                      value={form.buildingId || ''}
                      onValueChange={(v) => {
                        setField('buildingId', v ?? '')
                      }}
                      items={[
                        { label: t('companies.selectBuilding'), value: '' },
                        ...allBuildings.map((building) => ({
                          label: `${building.name} - ${building.city}`,
                          value: building.id,
                        })),
                      ]}
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <Label>{t('companies.wifiNetworks')}</Label>
                    {allNetworks.length === 0 ? (
                      <Text as="p" variant="secondary" size="sm">
                        {t('companies.noNetworksAvailable')}
                      </Text>
                    ) : (
                      <div className="flex flex-col gap-2">
                        {allNetworks.map((network) => (
                          <label
                            key={network.id}
                            className="flex cursor-pointer items-center gap-2"
                          >
                            <input
                              type="checkbox"
                              checked={form.wifiNetworkIds.includes(network.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setForm({
                                    ...form,
                                    wifiNetworkIds: [...form.wifiNetworkIds, network.id],
                                  })
                                } else {
                                  setForm({
                                    ...form,
                                    wifiNetworkIds: form.wifiNetworkIds.filter(
                                      (nid) => nid !== network.id,
                                    ),
                                  })
                                }
                              }}
                            />
                            <span className="text-sm">
                              {network.name} — {network.securityType}
                              {network.vlanId ? ` · VLAN ${network.vlanId}` : ''}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-1">
                    <Label>{t('companies.schedules')}</Label>
                    {allSchedules.length === 0 ? (
                      <Text as="p" variant="secondary" size="sm">
                        {t('companies.noSchedulesAvailable')}
                      </Text>
                    ) : (
                      <div className="flex flex-col gap-2">
                        {allSchedules.map((schedule) => (
                          <label
                            key={schedule.id}
                            className="flex cursor-pointer items-center gap-2"
                          >
                            <input
                              type="checkbox"
                              aria-label={schedule.name}
                              checked={scheduleIds.includes(schedule.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setScheduleIds([...scheduleIds, schedule.id])
                                } else {
                                  setScheduleIds(scheduleIds.filter((sid) => sid !== schedule.id))
                                }
                              }}
                            />
                            <span className="text-sm">{schedule.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-1">
                    <Switch
                      label={t('companies.openEnrollment.label')}
                      checked={form.openEnrollment}
                      onCheckedChange={(next: boolean) => setField('openEnrollment', next)}
                    />
                    <Text as="p" variant="secondary" size="sm">
                      {t('companies.openEnrollment.helperText')}
                    </Text>
                  </div>

                  <div className="flex flex-col gap-1">
                    <Switch
                      label={t('companies.skipEmailVerification.label')}
                      checked={form.skipEmailVerification}
                      onCheckedChange={(next: boolean) => setField('skipEmailVerification', next)}
                    />
                    <Text as="p" variant="secondary" size="sm">
                      {t('companies.skipEmailVerification.helperText')}
                    </Text>
                  </div>
                </div>
              )}

              {activeTab === 'selfEnrollment' && isEdit && id && (
                <div className="pt-2">
                  <CompanyEnrollmentPanel companyId={id} />
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" type="button" onClick={() => navigate('/companies')}>
                  {t('companies.cancel')}
                </Button>
                <Button variant="primary" type="submit" loading={submitting}>
                  {t('companies.save')}
                </Button>
              </div>
            </form>
          </LayerCard.Primary>
        </LayerCard>
      )}
    </div>
  )
}
